package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.Map
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(AppNotification.Companion.Serializer::class)
public class AppNotification : Resource, AppNotificationSchema {
  override val `companion`: Resource.CompanionObj<AppNotification> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var notificationType: String?
    get() = if (attributes.containsKey("type")) {
      attributes["type"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["type"] = value
    }

  override var `data`: Map<String, Any?>
    get() = if (attributes.containsKey("data")) {
      attributes["data"] as Map<String, Any?>
    } else {
      super.data
    }
    set(`value`) {
      attributes["data"] = value
    }

  override var isRead: Boolean
    get() = if (attributes.containsKey("is_read")) {
      attributes["is_read"] as Boolean
    } else {
      super.isRead
    }
    set(`value`) {
      attributes["is_read"] = value
    }

  override var readAt: String?
    get() = if (attributes.containsKey("read_at")) {
      attributes["read_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["read_at"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  public constructor(
    notificationType: String? = null,
    `data`: Map<String, Any?>? = null,
    isRead: Boolean? = null,
    readAt: String? = null,
    createdAt: String? = null,
  ) {
    if (notificationType != null) {
      attributes["type"] = notificationType
    }
    if (`data` != null) {
      attributes["data"] = `data`
    }
    if (isRead != null) {
      attributes["is_read"] = isRead
    }
    if (readAt != null) {
      attributes["read_at"] = readAt
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
  }

  public companion object : Resource.CompanionObj<AppNotification> {
    override val resourceType: String = "Notification"

    override val endpoint: String = "notifications"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> AppNotification = { AppNotification() }

    override val schema: KClass<AppNotificationSchema> = AppNotificationSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<AppNotification>.() -> Unit = {}): Scope<AppNotification> = Scope(AppNotification::class, options)

    public final suspend fun all(): CollectionProxy<AppNotification> = scope().all()

    public final suspend fun find(id: String): RecordProxy<AppNotification> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<AppNotification?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<AppNotification> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<AppNotification?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<AppNotification> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<AppNotification?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<AppNotification> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<AppNotification> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<AppNotification> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<AppNotification> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<AppNotification> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<AppNotification> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<AppNotification> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<AppNotification> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<AppNotification> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<AppNotification> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<AppNotification>()
  }
}
